/**
 * The React half of the global reactive store.
 *
 * `src/core/store-events.ts` announces every write; this is what a screen
 * subscribes with. One rule holds the whole thing together: **a data hook
 * re-reads on every announcement, whoever made it**. Assign a convoy on the
 * tasks board and the vehicle roster, the soldier roster and the dashboard
 * counters all re-read on the same tick — no page reload, no pull-to-refresh,
 * and no component having to know which other component wrote.
 *
 * Two details that matter in practice:
 *
 * - **Coalescing.** One user action often lands several writes (a task and its
 *   timeline row, a convoy and its ledger entries). The announcements are
 *   collapsed into a single re-read on the next microtask, so four screens do
 *   not run four queries each for one tap.
 * - **A live handler.** The callback is held in a ref, so a hook whose reload
 *   closure changes every render does not resubscribe every render either.
 */
import { useEffect, useRef } from 'react';
import { subscribeToStore } from '../core/store-events';
import type { StoreChange, StoreDomain } from '../core/store-events';

/**
 * Runs `handler` after any local write. Passing `domains` narrows it to the
 * parts of the database the caller actually reads; passing nothing hears
 * everything, which is what the shell-level hooks want.
 */
export function useStoreEvents(
  handler: () => void | Promise<void>,
  domains?: readonly StoreDomain[],
): void {
  const latest = useRef(handler);
  latest.current = handler;

  // The array identity changes every render; its contents do not.
  const key = domains ? [...domains].sort().join(',') : '';

  useEffect(() => {
    const wanted = key ? new Set(key.split(',')) : null;
    let queued = false;

    const onChange = (change: StoreChange) => {
      if (wanted && !wanted.has(change.domain)) return;
      if (queued) return;
      queued = true;
      // Collapses the burst of announcements one action makes into one re-read.
      queueMicrotask(() => {
        queued = false;
        void latest.current();
      });
    };

    return subscribeToStore(onChange);
  }, [key]);
}
